/*
 * Generic validation types for Javascript primitives such as strings, numbers, and booleans.
 */

import { isMatch } from "date-fns";
import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

/* Date part from ISO format YYYY-MM-dd */
export const isoDateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

/* HH:mm, 24 hour clock */
export const twentyFourHourTimeRegex = /^([01]\d|2[0-3]):?([0-5]\d)$/;

/* Time part from ISO format HH:mm */
const isoTimeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Unlike its name would suggest, trimmed string will both trim the string and
 * replace multiple spaces within it with a single space. If you are looking for a
 * string that is only trimmed, use `z.string().trim()` instead.
 */
export const TrimmedString = z
    .string()
    .transform((s) => s.trim().replaceAll(/\s\s+/g, " "));

export const EmptyString = z.string().length(0);
export const VeryShortString = z.string().max(20);
export const ShortString = z.string().max(100);
export const LongString = z.string().max(10000);
export const MediumLengthString = z.string().max(200);
export const MediumLongString = z.string().max(1024);
export const ISODate = z.string().regex(isoDateRegex, "Invalid YYYY-MM-dd date string");
export const ISOTime = z.string().regex(isoTimeRegex, "Invalid HH:mm time string");
export const ISODateTime = z.iso.datetime({ offset: true });
export type ISODateTime = z.infer<typeof ISODateTime>;

export const ValidDate = z.date({ error: "Date is invalid" });

export const FirebaseId = z.string().regex(/^[A-Za-z0-9]{20}$/, "Invalid id.");
export type FirebaseId = z.infer<typeof FirebaseId>;

export const AgentId = z.string().regex(/^[A-Za-z0-9]{28}$/, "Invalid agent id.");
export type AgentId = z.infer<typeof AgentId>;

export const MemberId = z.string().regex(/^[A-Fa-f0-9]{64}$/, "Invalid member id.");
export type MemberId = z.infer<typeof MemberId>;

export const AlbertineServiceAgentSenderId = "AlbertineServiceAgent";
export const AlbertineServiceAgentSenderName = "Albertine";
export const AlbertineServiceAgentId = z.literal(AlbertineServiceAgentSenderId);

export const NonNegativeNumber = z.number().min(0);
export type NonNegativeNumber = z.infer<typeof NonNegativeNumber>;
export const UrlString = EmptyString.or(
    z.url().refine((s) => s.startsWith("http://") || s.startsWith("https://")),
);
export const GeoPoint = z.object({
    latitude: z.number().optional(),
    longitude: z.number().optional(),
});

export type GeoPoint = z.infer<typeof GeoPoint>;
export const Rating = z.int().min(1).max(5).optional().nullable();

export const NumericStringOrNumber = z.coerce.string();

const Crop = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    unit: z.enum(["px", "%"]),
});

export const PixelCrop = Crop.omit({ unit: true }).and(
    z.object({ unit: z.enum(["px"]) }),
);
export const PercentCrop = Crop.omit({ unit: true }).and(
    z.object({ unit: z.enum(["%"]) }),
);
export type PixelCrop = z.infer<typeof PixelCrop>;
export type PercentCrop = z.infer<typeof PercentCrop>;

const dateRegex = /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/;
export const LocalDate = z.string().refine((value) => {
    if (!dateRegex.test(value)) {
        return false;
    }
    if (isMatch(value, "yyyy-MM-dd")) {
        return true;
    }
    return false;
}, "invalid date format");
export type LocalDate = z.infer<typeof LocalDate>;

export const LocalTime = z.string().refine((value) => {
    if (!isoTimeRegex.test(value)) {
        return false;
    }
    if (isMatch(value, "HH:mm")) {
        return true;
    }
    return false;
}, "invalid time format");

export const FirstName = z
    .string({ error: "First name is required" })
    .trim()
    .min(2, { error: "First name is required" })
    .refine((s) => /^[\u00c0-\u01ffa-zA-Z'-\s]+$/.test(s), {
        error: "First name should not contain special characters",
    });

export const LastName = z
    .string({ error: "Last name is required" })
    .trim()
    .min(2, { error: "Last name is required" })
    .refine((s) => /^[\u00c0-\u01ffa-zA-Z'-\s]+$/.test(s), {
        error: "Last name should not contain special characters",
    });

export const PhoneNumber = z.string().refine((t) => isValidPhoneNumber(t), {
    error: "Phone number is invalid",
});
export type PhoneNumber = z.infer<typeof PhoneNumber>;

export const Email = z
    .email({ error: "Email address is invalid" })
    .refine((e) => e.toLowerCase() === e, "Email address is invalid");
export type Email = z.infer<typeof Email>;
